/* ==========================================================================
   ux-reveal — the scroll reveal for the ux- sections that ask for it.

   assets/theme.css already holds every [data-aos] element back and shows it
   again once that element carries .aos-animate, but both halves of that are
   tied to the store-wide "Enable animations" setting: without it
   layout/theme.liquid renders no .aos-initialized on <body> and theme.js
   never starts its observer, so a ux- section that carries [data-aos] on its
   own markup never moves. This runs that one reveal for the sections that opt
   in with [data-ux-reveal], on the theme's own CSS, its own vocabulary
   (hero / img-in / data-aos-order / data-aos-duration / data-aos-easing) and
   its own observer settings, so the two can never disagree:

   - the root gets .aos-initialized, which is the whole of what the theme's
     CSS needs to hold its [data-aos] elements back;
   - an observer adds .aos-animate on the element's first intersection and
     stops watching it — assets/theme.dev.js's own thresholds and rootMargin,
     so an element enters identically whichever half of the theme drives it;
   - nothing is held back before this file runs, so a visitor without
     JavaScript, or a failed load, sees the whole page;
   - nothing runs where the theme's own init already has the page (the
     setting is on), where prefers-reduced-motion asks for no motion, or
     where there is no IntersectionObserver — the elements then stay put;
   - a section the theme editor re-renders is started again on
     `shopify:section:load`, and its observer is dropped on `section:unload`.

   Loaded once, from snippets/head.liquid, beside theme.js.
   ========================================================================== */

(function () {
  'use strict';

  const REVEAL_ROOT = '[data-ux-reveal]';
  const ANIMATED_ITEM = '[data-aos]';
  const INITIALIZED_CLASS = 'aos-initialized';
  const ANIMATED_CLASS = 'aos-animate';

  /* theme.dev.js's own config: an element enters at the viewport's edge. */
  const OBSERVER_CONFIG = {
    root: null,
    rootMargin: '0px',
    threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
  };

  /* One observer per opted-in section, so the editor can take a section's
     observer down without touching another's. */
  const observers = new WeakMap();

  function onIntersect(entries, observer) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add(ANIMATED_CLASS);

      /* The element is revealed once: it is never held back again, so the
         next scroll past it is not a second entrance. */
      observer.unobserve(entry.target);
    });
  }

  function start(root) {
    if (observers.has(root)) return;

    const items = root.querySelectorAll(ANIMATED_ITEM);

    if (!items.length) return;

    const observer = new IntersectionObserver(onIntersect, OBSERVER_CONFIG);

    observers.set(root, observer);
    root.classList.add(INITIALIZED_CLASS);

    items.forEach((item) => observer.observe(item));
  }

  function stop(root) {
    const observer = observers.get(root);

    if (!observer) return;

    observer.disconnect();
    observers.delete(root);
  }

  function revealRoots(scope) {
    const roots = Array.from(scope.querySelectorAll(REVEAL_ROOT));

    /* The theme editor hands the loaded section's wrapper, but a scope can be
       the root itself; querySelectorAll only ever looks below it. */
    if (scope.matches && scope.matches(REVEAL_ROOT)) roots.push(scope);

    return roots;
  }

  function startAll(scope) {
    /* The merchant has the theme's own reveal on: theme.js owns the page. */
    if (document.body.classList.contains(INITIALIZED_CLASS)) return;

    /* The theme's CSS escape for ux elements covers this too; not starting is
       simpler than holding elements back and handing them straight over. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!window.IntersectionObserver) return;

    revealRoots(scope).forEach(start);
  }

  document.addEventListener('shopify:section:load', (event) => startAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    revealRoots(event.target).forEach(stop);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => startAll(document), { once: true });
  } else {
    startAll(document);
  }
})();
